<template>
    <div>
        <h2>{{ __('Preferences') }}</h2>
        <p>for {{ user.name }} ({{ user.email }})</p>
        <div class="card" v-if="preferences && Object.keys(preferences).length">
            <div class="card-body">
                <div class="d-flex justify-content-center align-items-center">
                    <div class="input-and-submit-wrapper form-group">
                        <div class="form-icon">
                            <i class="fas fa-search"></i>
                        </div>
                        <input type="text" class="has-icon form-control" name="group-name" id="group-name"
                            v-model="searchTerm" :placeholder="__('Search by property name')" />
                    </div>
                </div>

                <form @submit.prevent="storePreferences">
                    <div class="table-responsive">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>{{ __('Hotel') }}</th>
                                    <th>{{ __('Preferred Languages') }}</th>
                                    <th>{{ __('Performance Report Emails') }}</th>
                                    <th>{{ __('Insights Notifications') }}</th>
                                    <th>{{ __('Goals Progress Notifications') }}</th>
                                    <th>{{ __('Feedback Breakdown') }}</th>
                                    <th>{{ __('Auto-Expand Manager Response') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(team, teamId) in pagedPreferences" :key="teamId">
                                    <td class="hotel-name" :title="team.team_name">
                                        {{ team.team_name }}
                                    </td>
                                    <td style="width: 100%;">
                                        <select v-model="team.preferred_language" class="form-control">
                                            <option v-for="(langName, langCode) in languages" :key="langCode"
                                                :value="langCode">
                                                {{ langName }}
                                            </option>
                                        </select>
                                    </td>
                                    <td>
                                        <div class="row">
                                            <div class="col-auto custom-control custom-switch"
                                                v-for="(period, index) in ['daily', 'weekly', 'monthly']" :key="index">
                                                <input type="checkbox" class="custom-control-input"
                                                    v-bind:true-value="1" v-bind:false-value="0"
                                                    v-model="team[`report_${period}`]"
                                                    :id="`${period}Report${teamId}`" />
                                                <label class="custom-control-label" :for="`${period}Report${teamId}`">
                                                    {{ __(period.charAt(0).toUpperCase() + period.slice(1)) }}
                                                </label>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="custom-control custom-switch">
                                            <input type="checkbox" class="custom-control-input" v-bind:true-value="1"
                                                v-bind:false-value="0" v-model="team.insight_notification"
                                                :id="'insightNotification' + teamId" />
                                            <label class="custom-control-label"
                                                :for="'insightNotification' + teamId"></label>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="custom-control custom-switch">
                                            <input type="checkbox" class="custom-control-input" v-bind:true-value="1"
                                                v-bind:false-value="0" v-model="team.goal_notification"
                                                :id="'goalNotification' + teamId" />
                                            <label class="custom-control-label"
                                                :for="'goalNotification' + teamId"></label>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="custom-control custom-switch">
                                            <input type="checkbox" class="custom-control-input" v-bind:true-value="1"
                                                v-bind:false-value="0" v-model="team.feedback_breakdown"
                                                :id="'feedbackBreakdown' + teamId" />
                                            <label class="custom-control-label"
                                                :for="'feedbackBreakdown' + teamId"></label>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="custom-control custom-switch">
                                            <input type="checkbox" class="custom-control-input" v-bind:true-value="1"
                                                v-bind:false-value="0" v-model="team.auto_expand_reviews"
                                                :id="'autoExpandReviews' + teamId" />
                                            <label class="custom-control-label"
                                                :for="'autoExpandReviews' + teamId"></label>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                        <button type="submit" class="btn btn-primary col-auto">{{ __('Save') }}</button>
                        <pagination :last_page="totalPages" :current_page="page" :num_items="pagedPreferences.length"
                            :num_total="filteredPreferences.length"></pagination>
                        <div class="col-auto"></div>
                    </div>
                </form>
            </div>
        </div>
        <div v-else>
            <div class="loader d-block text-center">
                <p><i class="fas fa-circle-notch fa-spin"></i></p>
                <p>Loading..</p>
            </div>
        </div>
    </div>
</template>

<script>
import * as _ from 'lodash';
export default {
    name: 'UserPreferences',
    props: ['languages'],
    data() {
        return {
            preferences: null,
            page: 1,
            pageSize: 5,
            user: null,
            originalPreferences: null,
            searchTerm: '',
        };
    },
    created() {
        this.user = Spark.state.user;
        this.fetchPreferences();
        this.$on('change-page', (page) => {
            this.page = page;
        });
    },
    watch: {
        searchTerm() {
            this.page = 1;
        },
    },
    computed: {
        filteredPreferences() {
            if (!this.preferences) return [];
            return Object.values(this.preferences).filter(team =>
                team.team_name.toLowerCase().includes(this.searchTerm.toLowerCase())
            );
        },
        pagedPreferences() {
            const startIndex = (this.page - 1) * this.pageSize;
            return this.filteredPreferences.slice(startIndex, startIndex + this.pageSize);
        },
        totalPages() {
            const totalItems = this.filteredPreferences.length;
            return Math.ceil(totalItems / this.pageSize);
        },
        modifiedPreferences() {
            const updates = {};
            for (const teamId in this.preferences) {
                const currentSettings = this.preferences[teamId];
                const originalSettings = this.originalPreferences[teamId];

                if (!_.isEqual(currentSettings, originalSettings)) {
                    updates[teamId] = currentSettings;
                }
            }
            return updates;
        },
    },
    methods: {
        async fetchPreferences() {
            try {
                const response = await axios.get('/user-settings');
                this.preferences = response.data;
                this.originalPreferences = _.cloneDeep(this.preferences);
            } catch (error) {
                console.error('Error fetching preferences:', error);
            }
        },
        async storePreferences() {
            if (Object.keys(this.modifiedPreferences).length === 0) {
                return;
            }
            try {
                const response = await axios.post('/user-settings', this.modifiedPreferences);
                this.showAlert('', response.data.message, 'success');
            } catch (error) {
                console.error('Error saving preferences:', error);
                this.showAlert('', error.message, 'error');
            }
        },
        showAlert(title, text, type) {
            swal({
                title: title,
                text: text,
                type: type,
                confirmButtonText: "OK",
            }, function () {
                // Reloading for browser to pickup language change
                location.reload();
            });
        },
    },
};
</script>

<style scoped>
table th,
table td {
    vertical-align: middle !important;
    text-align: center !important;
}
.hotel-name {
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap:  balance;
    cursor: pointer;
}
</style>